import React from 'react';

import { MISSING } from 'common/helpers/format';
import * as S from './styles';

interface IProps {
  /** The value. Empty string, null and undefined all count as absent. */
  children?: React.ReactNode;
  /**
   * What to say when it is absent. Say what is missing:
   * `MISSING.assigned` for something nobody has been given yet,
   * `MISSING.provided` for something nobody filled in,
   * `MISSING.none` (an em dash) when there is nothing worth saying.
   */
  fallback?: React.ReactNode;
  /** Renders in --font-mono with tabular numerals. For IDs and timestamps. */
  mono?: boolean;
  className?: string;
  style?: object;
}

const isAbsent = (v: React.ReactNode): boolean =>
  v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));

/**
 * A single value in a detail grid or a table cell.
 *
 * It exists so that "there is nothing here" is said in words rather than with
 * a bare `-`, which the panel used in roughly ninety places and which reads
 * identically whether the field is empty, not applicable, or broken.
 *
 *   <Value fallback={MISSING.assigned}>{ticket.number}</Value>
 *   <Value mono>{formatDateTime(ticket.createdAt)}</Value>
 *
 * Absent renders the fallback in --text-faint at regular weight, so a missing
 * value is visibly quieter than a present one instead of the same size.
 */
const Value: React.FC<IProps> = ({ children, fallback = MISSING.none, mono, className, style }) => {
  if (isAbsent(children)) {
    return (
      <S.Empty className={className} style={style}>
        {fallback}
      </S.Empty>
    );
  }

  if (mono) {
    return (
      <S.Mono className={className} style={style}>
        {children}
      </S.Mono>
    );
  }

  return (
    <span className={className} style={style}>
      {children}
    </span>
  );
};

export default Value;
